import React, { useEffect } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { feedbackSchema } from "../../../helpers/validation-schema";
import {
  addFeedBack,
  editFeedback,
  getFeedback,
  getFeedbackCount,
} from "../services/feedback-services";
import { resetError, resetState } from "../controller/feedback-slice";
import Switch from "react-switch";
import {
  capitalizeFirstLetter,
  handleInput,
  Offsymbol,
  OnSymbol,
} from "../../../helpers/helpers";
import {
  NAME_REGEX,
  PHONE_NUMBER_MAXLENGTH,
} from "../../../constants/constants";
import CustomLabel from "../../../components/CustomLabel";
export const AddFeedBack = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const FeedbackId = pathname.split("/")[3];
  const success = useSelector((state) => state.feedback.success);
  const feedback = useSelector((state) => state.feedback.feedbacks);
  const loading = useSelector((state) => state.feedback.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.feedback.successMessage);
  const errorMessage = useSelector((state) => state.feedback.errorMessage);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(feedbackSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getFeedbackCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [route, setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getFeedback(FeedbackId));
    }
  }, [FeedbackId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/feedback?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (feedback) {
      setValue("name", feedback?.name);
      setValue("sorting", feedback?.sorting);
      setValue("is_user_text_input", feedback?.is_user_text_input || false);
    }
  }, [feedback, setValue]);

  const onSubmit = (data) => {
    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        name: capitalizeFirstLetter(data.name),
      };

      dispatch(addFeedBack(formattedData));
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };

      dispatch(editFeedback({ id: FeedbackId, data: formattedData }));
    }
  };
  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Feedback"
                  : route === "view"
                  ? "View Feedback"
                  : "Create Feedback"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Feedback" isRequired>
                        Feedback
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Feedback"
                              invalid={!!errors.name}
                              maxLength={NAME_MAXLENGTH}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={2}>
                    <Label for="is_user_text_input">User Text Input:</Label>
                  </Col>
                </Row>
                <Row>
                  <Col md={1}>
                    <FormGroup>
                      <Controller
                        name="is_user_text_input"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="isUserSwitch"
                            onChange={(checked) => field.onChange(checked)}
                            checked={field.value}
                            uncheckedIcon={<Offsymbol />}
                            checkedIcon={<OnSymbol />}
                            className="me-1 mb-sm-8 mb-2"
                            onColor="#02a499"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/feedback?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button type="submit" color="primary">
                          {route === "edit"
                            ? "Edit Feedback"
                            : "Create Feedback"}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddFeedBack;
